import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Button, Form, message, notification } from "antd";
import dayjs from "dayjs";
import { InfoCircleOutlined, EditOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import InputCard from "../../../Components/Cards/InputCard";
import FuelType from "../../../Components/Cards/FuelTypeCard";
import "./style.css";
import GenericOperatingHours from "../../../Components/Tables/GenericOperatingHourTables";
import GenericDocumentManagerTable from "../../../Components/Tables/GenericDocumentManagerTable";
import LocationMap from "../../../Components/Map/LocationPicker";
import {
  getVendorDetails,
  updateVendorDetails,
  deleteVendorDocument,
} from "../../../API/FuelVendor/VendorDetails";
import {
  getFuelTypes,
  getVendorTypes,
  getFuelBrands,
  getDocumentCategories,
} from "../../../API/Config";
import usePageAccess from "../../../Utlis/usePageAccess";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar) || avatar.startsWith("blob:")) {
    return avatar;
  }
  return `${BACKEND_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
};

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  const fieldErrors = Object.values(data)
    .flat()
    .filter((msg) => typeof msg === "string");

  return fieldErrors.length ? fieldErrors.join(" ") : fallback;
};

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "-";

const mapDocument = (doc) => ({
  key: String(doc.id),
  id: doc.id,
  name: doc.document_name || (doc.file ? doc.file.split("/").pop() : doc.category) || "-",
  category: doc.category || "-",
  allowedFormats: doc.allowed_formats || [],
  date: formatDate(doc.uploaded_at),
  expiryDate: formatDate(doc.expiry_date),
  fileUrl: resolveAvatarUrl(doc.file),
});

const VendorDetail = ({ setUser }) => {
  const { canEdit } = usePageAccess("vendor-details");
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const watchedLatitude = Form.useWatch("latitude", form);
  const watchedLongitude = Form.useWatch("longitude", form);

  const [avatar, setAvatar] = useState(null);
  const [selectedFuels, setSelectedFuels] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [availableFuelTypes, setAvailableFuelTypes] = useState([]);
  const [vendorTypeOptions, setVendorTypeOptions] = useState([]);
  const [fuelBrandOptions, setFuelBrandOptions] = useState([]);
  const [operatingHours, setOperatingHours] = useState([]);
  const [documentCategories, setDocumentCategories] = useState([]);

  const fileInputRef = useRef(null);
  const location = useLocation();
  const [incomingData, setIncomingData] = useState(
    location.state?.userInfo || null,
  );
  const [isEditing, setIsEditing] = useState(
    canEdit && !!location.state?.userInfo,
  );

  useEffect(() => {
    if (location.state?.userInfo) return;

    const fetchProfile = async () => {
      try {
        const response = await getVendorDetails();

        if (Math.floor(response?.status / 100) === 2) {
          const vendor = response.data?.data || {};

          setIncomingData({
            legal_name: vendor.legal_name,
            brand_name: vendor.brand_name,
            tax_id: vendor.tax_id,
            registration_date: vendor.registration_date,
            date_incorporated: vendor.date_incorporated,
            vendor_type: vendor.vendor_type,
            contact_email: vendor.contact_email,
            contact_phone: vendor.contact_phone,
            registration_contact_phone: vendor.registration_contact_phone,
            address: vendor.address,
            currency: vendor.currency || "JMD",
            avatar: resolveAvatarUrl(vendor.avatar),
            primary_contact_person: vendor.primary_contact_person,
            fuel_types_offered: vendor.fuel_types_offered,
            documents: vendor.documents,
            station_name: vendor.station_name,
            station_id: vendor.station_id,
            station_address: vendor.station_address,
            fuel_brand: vendor.fuel_brand?.id ?? vendor.fuel_brand,
            latitude: vendor.latitude,
            longitude: vendor.longitude,
            bank_details: vendor.bank_details,
            operating_hours: vendor.operating_hours,
            wipay_account_number: vendor.wipay_account_number,
            wipay_api_key: vendor.wipay_api_key,
          });
        } else {
          setIsEditing(true);
        }
      } catch (err) {
        setIsEditing(true);
      }
    };

    fetchProfile();
  }, []);

  useEffect(() => {
    if (!isEditing || availableFuelTypes.length) return;

    const fetchFuelTypes = async () => {
      try {
        const response = await getFuelTypes();
        const list = Array.isArray(response?.data)
          ? response.data
          : response?.data?.data || response?.data?.results || [];

        if (Math.floor(response?.status / 100) === 2 && Array.isArray(list)) {
          setAvailableFuelTypes(list);
        }
      } catch (err) {
        console.error("Failed to load fuel types:", err);
      }
    };

    fetchFuelTypes();
  }, [isEditing, availableFuelTypes.length]);

  useEffect(() => {
    if (!isEditing || vendorTypeOptions.length) return;

    const fetchVendorTypes = async () => {
      try {
        const response = await getVendorTypes();
        const list = Array.isArray(response?.data)
          ? response.data
          : response?.data?.data || response?.data?.results || [];

        if (Math.floor(response?.status / 100) === 2 && Array.isArray(list)) {
          setVendorTypeOptions(
            list.map((type) =>
              typeof type === "string"
                ? type
                : { label: type.name, value: type.name },
            ),
          );
        }
      } catch (err) {
        console.error("Failed to load vendor types:", err);
      }
    };

    fetchVendorTypes();
  }, [isEditing, vendorTypeOptions.length]);

  useEffect(() => {
    if (fuelBrandOptions.length) return;

    const fetchFuelBrands = async () => {
      try {
        const response = await getFuelBrands();
        const list = Array.isArray(response?.data)
          ? response.data
          : response?.data?.data || response?.data?.results || [];

        if (Math.floor(response?.status / 100) === 2 && Array.isArray(list)) {
          setFuelBrandOptions(
            list.map((brand) =>
              typeof brand === "string"
                ? { label: brand, value: brand }
                : { label: brand.name, value: brand.id ?? brand.name },
            ),
          );
        }
      } catch (err) {
        console.error("Failed to load fuel brands:", err);
      }
    };

    fetchFuelBrands();
  }, [fuelBrandOptions.length]);

  useEffect(() => {
    if (documentCategories.length) return;

    const fetchDocumentCategories = async () => {
      const res = await getDocumentCategories("fuel-vendor");
      const list = res?.data?.data;
      if (res?.status === 200 && Array.isArray(list)) {
        setDocumentCategories(
          list.map((cat) => ({
            label: cat.document_name,
            value: cat.document_name,
            allowedFormats: cat.allowed_formats,
          })),
        );
      }
    };

    fetchDocumentCategories();
  }, [documentCategories.length]);

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const MAX_FILENAME_LENGTH = 100;

  const shortenFileName = (file, maxLength = MAX_FILENAME_LENGTH) => {
    if (file.name.length <= maxLength) return file;

    const dotIndex = file.name.lastIndexOf(".");
    const ext = dotIndex !== -1 ? file.name.slice(dotIndex) : "";
    const base = dotIndex !== -1 ? file.name.slice(0, dotIndex) : file.name;
    const truncatedBase = base.slice(0, Math.max(1, maxLength - ext.length));

    return new File([file], `${truncatedBase}${ext}`, { type: file.type });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const shortenedFile = shortenFileName(file);
    const previewUrl = URL.createObjectURL(shortenedFile);
    setAvatar(previewUrl);

    if (!incomingData) return;

    try {
      const formData = new FormData();
      formData.append("avatar", shortenedFile);

      const response = await updateVendorDetails(formData);

      if (Math.floor(response?.status / 100) === 2) {
        const updated = response.data?.data;
        setAvatar(resolveAvatarUrl(updated?.avatar) || previewUrl);
        message.success(
          response?.data?.message || "Avatar updated successfully",
        );
      } else {
        message.error(
          getErrorMessage(response?.data, "Unexpected response from server."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to update avatar."),
      );
    }
  };
  const initialData = {
    legalEntityName: incomingData?.legal_name,
    brandName: incomingData?.brand_name,
    taxId: incomingData?.tax_id,
    registrationDate: incomingData?.registration_date
      ? dayjs(incomingData.registration_date)
      : undefined,
    dateIncorporated: incomingData?.date_incorporated
      ? dayjs(incomingData.date_incorporated)
      : undefined,
    vendorType: incomingData?.vendor_type,
    companyContactNumber: incomingData?.contact_phone,
    phoneNumber: incomingData?.registration_contact_phone,
    hqAddress: incomingData?.address,
    primaryContact: incomingData?.primary_contact_person,
    businessEmail: incomingData?.contact_email,
    stationName: incomingData?.station_name,
    stationId: incomingData?.station_id,
    stationAddress: incomingData?.station_address,
    fuelBrand: incomingData?.fuel_brand,
    latitude: incomingData?.latitude,
    longitude: incomingData?.longitude,
    bankName: incomingData?.bank_details?.bank_name,
    bankAddress: incomingData?.bank_details?.bank_address,
    accountHolderName: incomingData?.bank_details?.account_holder_name,
    accountNumber: incomingData?.bank_details?.account_number,
    accountType: incomingData?.bank_details?.account_type,
    swiftCode: incomingData?.bank_details?.swift_code,
    currency: incomingData?.bank_details?.settlement_currency || "JMD",
    wipayAccountNumber: incomingData?.wipay_account_number,
    wipayApiKey: incomingData?.wipay_api_key,
  };

  useEffect(() => {
    form.setFieldsValue(initialData);

    if (incomingData?.fuel_types_offered) {
      setSelectedFuels(incomingData.fuel_types_offered.map((f) => f.name));
    } else if (incomingData?.selectedFuels) {
      setSelectedFuels(incomingData.selectedFuels);
    }
    if (Array.isArray(incomingData?.documents)) {
      setDocuments(incomingData.documents.map(mapDocument));
    }
    if (incomingData?.avatar) setAvatar(incomingData.avatar);
    if (Array.isArray(incomingData?.operating_hours)) {
      setOperatingHours(incomingData.operating_hours);
    }
  }, [incomingData]);

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (error) {
      message.error("Please check the form for missing fields.");
      return;
    }

    if (!incomingData) {
      if (!selectedFuels.length) {
        message.error("Please select at least one fuel type.");
        return;
      }
      if (!documents.length) {
        message.error("Please upload at least one document.");
        return;
      }
    }

    try {
      const formData = new FormData();
      formData.append("legal_name", values.legalEntityName || "");
      formData.append("brand_name", values.brandName || "");
      formData.append("tax_id", values.taxId || "");
      formData.append(
        "date_incorporated",
        values.dateIncorporated ? values.dateIncorporated.format("YYYY-MM-DD") : "",
      );
      formData.append("vendor_type", values.vendorType || "");
      formData.append("contact_email", values.businessEmail || "");
      formData.append("contact_phone", values.companyContactNumber || "");
      formData.append("registration_contact_phone", values.phoneNumber || "");
      formData.append("address", values.hqAddress || "");
      formData.append("primary_contact_person", values.primaryContact || "");
      formData.append("station_name", values.stationName || "");
      formData.append("station_id", values.stationId || "");
      formData.append("station_address", values.stationAddress || "");
      formData.append("fuel_brand_id", values.fuelBrand || "");
      formData.append("latitude", values.latitude || "");
      formData.append("longitude", values.longitude || "");
      formData.append("bank_name", values.bankName || "");
      formData.append("bank_address", values.bankAddress || "");
      formData.append("account_holder_name", values.accountHolderName || "");
      formData.append("account_number", values.accountNumber || "");
      formData.append("swift_code", values.swiftCode || "");
      formData.append("account_type", values.accountType || "");
      formData.append("settlement_currency", values.currency || "");
      formData.append("wipay_account_number", values.wipayAccountNumber || "");
      formData.append("wipay_api_key", values.wipayApiKey || "");
      formData.append("operating_hours", JSON.stringify(operatingHours));

      const fuelTypeIdByName = {};
      availableFuelTypes.forEach((f) => {
        fuelTypeIdByName[f.name] = f.id;
      });
      (incomingData?.fuel_types_offered || []).forEach((f) => {
        fuelTypeIdByName[f.name] = f.id;
      });
      const fuelTypeIds = selectedFuels
        .map((name) => fuelTypeIdByName[name])
        .filter((id) => id !== undefined);
      fuelTypeIds.forEach((id) => {
        formData.append("fuel_types_offered_ids", id);
      });

      const newDocuments = documents.filter((doc) => doc.file);
      newDocuments.forEach((doc) => {
        formData.append("documents", doc.file);
      });
      if (newDocuments.length > 0) {
        formData.append(
          "document_categories",
          JSON.stringify(newDocuments.map((doc) => doc.category)),
        );
        formData.append(
          "document_expiry_dates",
          JSON.stringify(newDocuments.map((doc) => doc.expiryDate || null)),
        );
      }

      const response = await updateVendorDetails(formData);

      if (Math.floor(response?.status / 100) !== 2) {
        message.error(
          getErrorMessage(response?.data, "Unexpected response from server."),
        );
        return;
      }

      notification.success({
        message: "Updated",
        description: response?.data?.message || "Profile updated successfully",
      });

      const vendor = response.data?.data || {};

      if (incomingData) {
        setIncomingData((prev) => ({
          ...prev,
          legal_name: vendor.legal_name,
          brand_name: vendor.brand_name,
          tax_id: vendor.tax_id,
          registration_date: vendor.registration_date,
          date_incorporated: vendor.date_incorporated,
          vendor_type: vendor.vendor_type,
          contact_email: vendor.contact_email,
          contact_phone: vendor.contact_phone,
          registration_contact_phone: vendor.registration_contact_phone,
          address: vendor.address,
          currency: vendor.currency || "JMD",
          primary_contact_person: vendor.primary_contact_person,
          fuel_types_offered: vendor.fuel_types_offered,
          station_name: vendor.station_name,
          station_id: vendor.station_id,
          station_address: vendor.station_address,
          fuel_brand: vendor.fuel_brand?.id ?? vendor.fuel_brand,
          latitude: vendor.latitude,
          longitude: vendor.longitude,
          bank_details: vendor.bank_details,
          operating_hours: vendor.operating_hours,
          documents: vendor.documents,
          wipay_account_number: vendor.wipay_account_number,
          wipay_api_key: vendor.wipay_api_key,
        }));
        if (Array.isArray(vendor.documents)) {
          setDocuments(vendor.documents.map(mapDocument));
        }
        setIsEditing(false);
      } else {
        navigate("/profile-vendor", {
          state: {
            updatedUser: {
              avatar: resolveAvatarUrl(vendor.avatar) || avatar,
              legal_name: vendor.legal_name,
              brand_name: vendor.brand_name,
              tax_id: vendor.tax_id,
              vendor_type: vendor.vendor_type,
              contact_email: vendor.contact_email,
              contact_phone: vendor.contact_phone,
              registration_contact_phone: vendor.registration_contact_phone,
              address: vendor.address,
              currency: vendor.currency || "JMD",
              selectedFuels,
              documents,
            },
          },
        });
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to update profile."),
      );
    }
  };

  const handleCancel = () => {
    form.setFieldsValue(initialData);
    setSelectedFuels(incomingData?.selectedFuels || []);
    setDocuments(
      Array.isArray(incomingData?.documents)
        ? incomingData.documents.map(mapDocument)
        : [],
    );
    setAvatar(incomingData?.avatar || null);

    if (incomingData) {
      setIsEditing(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const registrationFields = [
    {
      name: "legalEntityName",
      label: "Legal Entity Name",
      placeholder: "Enter Legal Entity Name",
      type: "text",
      fullWidth: true,
      rules: [{ required: true, message: "Legal entity name is required" }],
    },
    {
      name: "brandName",
      label: "Brand Name",
      placeholder: "Enter Brand Name",
      type: "text",
      rules: [{ required: true, message: "Brand name is required" }],
    },
    {
      name: "taxId",
      label: "Tax ID/VAT Number",
      placeholder: "Enter Tax ID/VAT Number",
      type: "text",
      rules: [{ required: true, message: "Tax ID is required" }],
    },
    {
      name: "dateIncorporated",
      label: "Date Incorporated",
      placeholder:
        incomingData && !isEditing ? "-" : "Select Date Incorporated",
      type: "date",
    },
    {
      name: "registrationDate",
      label: "Registration Date",
      placeholder:
        incomingData && !isEditing ? "-" : "Select Registration Date",
      type: "date",
    },
    {
      name: "vendorType",
      label: "Vendor Type",
      placeholder: "Select Vendor Type",
      type: "select",
      options: vendorTypeOptions,
      rules: [{ required: true, message: "Vendor type is required" }],
    },
    {
      name: "companyContactNumber",
      label: "Company Contact Number",
      placeholder: "Enter Company Contact Number",
      type: "phone",
      rules: [{ required: true, message: "Contact number is required" }],
    },
  ];

  const contactFields = [
    {
      name: "hqAddress",
      label: "Headquarters Address",
      placeholder: "Enter full headquarters address",
      type: "textarea",
      fullWidth: true,
      rules: [{ required: true, message: "Headquarters address is required" }],
    },
    {
      name: "primaryContact",
      label: "Primary Contact Person",
      placeholder: "Enter Full Name",
      type: "text",
      rules: [{ required: true, message: "Primary contact is required" }],
    },
    {
      name: "businessEmail",
      label: "Business Email",
      placeholder: "email@company.com",
      type: "text",
      rules: [
        { required: true, message: "Email is required" },
        { type: "email", message: "Enter a valid email" },
      ],
    },
    {
      name: "phoneNumber",
      label: "Phone Number",
      placeholder: "Enter Phone Number",
      type: "phone",
      rules: [{ required: true, message: "Phone number is required" }],
    },
  ];

  const bankingFields = [
    {
      name: "bankName",
      label: "Bank Name",
      placeholder: "Enter Bank Name",
      type: "text",
      rules: [{ required: true, message: "Bank name is required" }],
    },
    {
      name: "bankAddress",
      label: "Bank Address",
      placeholder: "Enter Bank Address",
      type: "text",
      rules: [{ required: true, message: "Bank address is required" }],
    },
    {
      name: "accountHolderName",
      label: "Account Holder Name",
      placeholder: "Enter Account Holder Name",
      type: "text",
      rules: [{ required: true, message: "Account holder name is required" }],
    },
    {
      name: "accountNumber",
      label: "Account Number",
      placeholder: "Enter Account Number",
      type: "text",
      rules: [{ required: true, message: "Account number is required" }],
    },
    {
      name: "accountType",
      label: "Account Type",
      placeholder: "Enter Account Type",
      type: "text",
      rules: [{ required: true, message: "Account type is required" }],
    },
    {
      name: "swiftCode",
      label: "SWIFT / BIC Code",
      placeholder: "e.g. HBLPKKAXXX",
      type: "text",
      rules: [{ required: true, message: "SWIFT/BIC code is required" }],
    },
    {
      name: "currency",
      label: "Currency",
      placeholder: "Select Currency",
      type: "currency",
      rules: [{ required: true, message: "Currency is required" }],
    },
    {
      name: "wipayAccountNumber",
      label: "WiPay Account Number",
      placeholder: "Enter WiPay Account Number",
      type: "text",
    },
    {
      name: "wipayApiKey",
      label: "WiPay API Key",
      placeholder: "Enter WiPay API Key",
      type: "text",
    },
  ];

  const stationFields = [
    {
      name: "stationName",
      label: "Station Name",
      placeholder: "Enter Station Name",
      type: "text",
      rules: [{ required: true, message: "Station name is required" }],
    },
    {
      name: "stationId",
      label: "Station ID",
      placeholder: "e.g. STN-2026-8810-X91",
      type: "text",
      rules: [{ required: true, message: "Station ID is required" }],
    },
    {
      name: "stationAddress",
      label: "Full Physical Address",
      placeholder: "e.g. 1 Business Parkway, Suite 100, Houston TX",
      type: "textarea",
      fullWidth: true,
      rules: [{ required: true, message: "Station address is required" }],
    },
    {
      name: "fuelBrand",
      label: "Fuel Brand",
      placeholder: "Select Fuel Brand",
      type: "select",
      options: fuelBrandOptions,
    },
    {
      name: "latitude",
      label: "Latitude",
      placeholder: "e.g. 40.712",
      type: "text",
      rules: [{ required: true, message: "Latitude is required" }],
    },
    {
      name: "longitude",
      label: "Longitude",
      placeholder: "e.g. -74.006",
      type: "text",
      rules: [{ required: true, message: "Longitude is required" }],
    },
  ];

  return (
    <div className="fv-signup edit-sign-up">
      <div className="fv-signup__card">
        {incomingData && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            {canEdit && !isEditing && (
              <Button onClick={handleEdit} icon={<EditOutlined />}>
                Edit Details
              </Button>
            )}
          </div>
        )}
        <Form form={form} layout="vertical">
          <div className="profile-card">
            <div className="profile-header">
              <div className="btn">
                <Button
                  type="default"
                  className="upload-btn"
                  onClick={handleButtonClick}
                  disabled={incomingData && !isEditing}
                >
                  Upload Photo
                </Button>
              </div>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
              />

              <div className="profile-avatar">
                {avatar ? (
                  <img
                    src={avatar}
                    alt="avatar"
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                    }}
                  />
                ) : (
                  "T"
                )}
              </div>
            </div>
            <div className="profile-body"></div>
          </div>

          <InputCard
            title="Fuel Vendor Registration"
            fields={registrationFields.map((field) => ({
              ...field,
              disabled: [
                "taxId",
                "companyContactNumber",
                "registrationDate",
              ].includes(field.name)
                ? true
                : !isEditing,
            }))}
          />
          <InputCard
            title="Contact Information"
            fields={contactFields.map((field) => ({
              ...field,
              disabled: field.name === "businessEmail" ? true : !isEditing,
            }))}
          />
          <InputCard
            title="Banking & Settlement"
            fields={bankingFields.map((field) => ({
              ...field,
              disabled: !isEditing,
            }))}
          />

          <InputCard
            title="Fuel Station Information"
            fields={stationFields.slice(0, 4).map((field) => ({
              ...field,
              disabled: !isEditing,
            }))}
          />
          <div className="fv-signup__gps-banner">
            <InfoCircleOutlined className="fv-signup__gps-icon" />
            <span>
              <div className="heading-banner">GPS Coordinate Requirement:</div>
              <div className="banner-sub-heading">
                Every Geo-GPS coordinates are mandatory. These are used to
                automatically validate fuel transactions ensuring accurate
                location verification.
              </div>
            </span>
          </div>
          <InputCard
            title=""
            fields={stationFields.slice(4).map((field) => ({
              ...field,
              disabled: !isEditing,
            }))}
          >
            <LocationMap
              latitude={watchedLatitude ? parseFloat(watchedLatitude) : null}
              longitude={watchedLongitude ? parseFloat(watchedLongitude) : null}
              label="Station Location"
              onLocationSelect={
                isEditing
                  ? ({ latitude, longitude }) =>
                      form.setFieldsValue({ latitude, longitude })
                  : undefined
              }
            />
          </InputCard>

          <FuelType
            fuelList={
              isEditing
                ? Array.from(
                    new Set([
                      ...availableFuelTypes.map((f) => f.name),
                      ...selectedFuels,
                    ]),
                  )
                : selectedFuels
            }
            selectedFuel={selectedFuels}
            onSelectFuel={setSelectedFuels}
            disabled={!isEditing}
          />
          <GenericOperatingHours
            value={operatingHours}
            onChange={setOperatingHours}
            disabled={!isEditing}
          />
          <GenericDocumentManagerTable
            showUploadOnly={true}
            setDocuments={setDocuments}
            initialDocuments={documents}
            disabled={!isEditing}
            documentTypes={documentCategories}
            onDeleteDocument={async (doc) => {
              if (!doc?.id) return;

              const res = await deleteVendorDocument(doc.id);

              if (Math.floor(res?.status / 100) !== 2) {
                throw new Error(
                  getErrorMessage(res?.data, "Failed to delete document."),
                );
              }
            }}
          />
        </Form>

        {(!incomingData || (canEdit && isEditing)) && (
          <div className="btns-row">
            <Button onClick={handleCancel}>Cancel</Button>
            <Button type="primary" className="save-btn" onClick={handleSubmit}>
              Save Changes
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorDetail;
